import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import TaskTimer from "@/components/TaskTimer";
import { TID } from "@/constants/testIds";
import { PRIORITY_META, PRIORITY_ORDER, priorityOf } from "@/constants/status";
import { Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function TaskDialog({ open, onOpenChange, users, existing, defaultAssignee, onSaved, onTaskCompleted }) {
  const { user } = useAuth();
  // A "manager" in the task-dialog sense = anyone with cross-user assignment
  // authority. Role=manager, is_admin (Om/Akanksha) and is_super_admin (Amit)
  // all qualify; regular editors get the self-assigned chip UI instead.
  const isManager = user?.role === "manager" || !!user?.is_super_admin || !!user?.is_admin;
  const editing = !!(existing && existing.id);
  const [form, setForm] = useState({
    title: "", description: "", assigned_to: "", assignees: [], start_date: "", deadline: "",
    nas_link: "", final_video_link: "", priority: false, priority_level: "normal", client: "", project_id: "", status: "not_started",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [manualHours, setManualHours] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [addingTime, setAddingTime] = useState(false);
  const [timerRefreshKey, setTimerRefreshKey] = useState(0);
  // Weekly-submission upload UI — rendered when the task has a `submission_group`
  // (weekly | social) or the legacy `is_weekly_report` flag. Two PDF slots per
  // task: `report` (last week) + `plan` (this week). Each slot is uploaded
  // independently via POST /api/tasks/{id}/submission/upload?slot=<slot>.
  // Task auto-completes on the backend when BOTH slots are filled.
  const [weeklyFiles, setWeeklyFiles] = useState({ report: null, plan: null });
  const [weeklyUploadingSlot, setWeeklyUploadingSlot] = useState("");
  // My current tracked total (seconds) — hydrated from GET /tasks/{id}/timer
  // so the "Edit total hours" input can prefill with what's already logged.
  const [myTrackedSeconds, setMyTrackedSeconds] = useState(0);

  // Client / Project directories loaded on open. `clients` is a distinct list
  // from every existing task + every project row. `projectsForClient` is
  // scoped to the currently selected client.
  const [clients, setClients] = useState([]);
  const [projectsForClient, setProjectsForClient] = useState([]);
  const [addingClient, setAddingClient] = useState(false);
  const [addingProject, setAddingProject] = useState(false);
  const [newClient, setNewClient] = useState("");
  const [newProject, setNewProject] = useState("");
  const [similarMatches, setSimilarMatches] = useState([]);

  useEffect(() => {
    if (open) {
      if (existing) {
        setForm({
          title: existing.title || "",
          description: existing.description || "",
          assigned_to: existing.assigned_to || "",
          assignees: (existing.assignees && existing.assignees.length ? existing.assignees : [existing.assigned_to]).filter(Boolean),
          start_date: existing.start_date ? existing.start_date.slice(0, 10) : "",
          deadline: existing.deadline ? existing.deadline.slice(0, 10) : "",
          nas_link: existing.nas_link || "",
          final_video_link: existing.final_video_link || "",
          priority: !!existing.priority,
          priority_level: priorityOf(existing),
          client: existing.client || "",
          project_id: existing.project_id || "",
          status: existing.status || "not_started",
        });
      } else {        setForm({
          title: "", description: "",
          assigned_to: !isManager ? user?.id || "" : (defaultAssignee || ""),
          assignees: !isManager && user?.id ? [user.id] : (defaultAssignee ? [defaultAssignee] : []),
          start_date: "", deadline: "", nas_link: "", final_video_link: "", priority: false, priority_level: "normal", client: "", project_id: "", status: "not_started",
        });
      }
      setWeeklyFiles({ report: null, plan: null });
    }
  }, [open, existing, defaultAssignee]);

  // Load the master client list once whenever the dialog opens. Cheap; small payload.
  useEffect(() => {
    if (!open) return;
    api.get("/projects/clients").then(({ data }) => setClients(data.clients || [])).catch(() => setClients([]));
  }, [open]);

  // Refresh the Project list whenever the selected Client changes.
  useEffect(() => {
    if (!open || !form.client) { setProjectsForClient([]); return; }
    api.get("/projects", { params: { client: form.client } })
      .then(({ data }) => setProjectsForClient(data || []))
      .catch(() => setProjectsForClient([]));
  }, [open, form.client]);

  // Live "similar open tasks" hint — debounced. Only for NEW tasks and when the
  // title is long enough to be meaningful. Scoped by client so unrelated
  // matches don't spam the picker.
  useEffect(() => {
    if (editing || !open) { setSimilarMatches([]); return; }
    const title = form.title.trim();
    if (title.length < 4) { setSimilarMatches([]); return; }
    const handle = setTimeout(() => {
      api.get("/tasks/similar", { params: { title, client: form.client || undefined, limit: 5 } })
        .then(({ data }) => setSimilarMatches(data.matches || []))
        .catch(() => setSimilarMatches([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [form.title, form.client, editing, open]);

  const handleCreateClient = () => {
    const name = newClient.trim();
    if (!name) { toast.error("Enter a client name"); return; }
    // We don't persist a client on its own — the client string is bound to the
    // task/project when it saves. Push it into the local list + select it.
    if (!clients.some((c) => c.toLowerCase() === name.toLowerCase())) {
      setClients((prev) => [...prev, name].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })));
    }
    setForm({ ...form, client: name, project_id: "" });
    setNewClient(""); setAddingClient(false);
  };

  const handleCreateProject = async () => {
    const name = newProject.trim();
    if (!form.client.trim()) { toast.error("Pick a client first"); return; }
    if (!name) { toast.error("Enter a project name"); return; }
    try {
      const { data } = await api.post("/projects", { client: form.client, name });
      // Add / dedupe into projectsForClient and select it.
      setProjectsForClient((prev) => {
        if (prev.some((p) => p.id === data.id)) return prev;
        return [...prev, { id: data.id, client: data.client, name: data.name, archived: false }]
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      });
      setForm({ ...form, project_id: data.id });
      setNewProject(""); setAddingProject(false);
      toast.success(data.reused ? "Linked to existing project" : "Project created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create project");
    }
  };

  // Every logged-in user can now CREATE a task (self-assigned when they're
  // not a manager). Any assignee (lead OR co-assignee) can EDIT an existing
  // task. Only managers keep the ability to reassign the roster.
  const isCoAssignee = !!editing && !!user?.id && (
    existing?.assigned_to === user.id ||
    (existing?.assignees || []).includes(user.id)
  );
  const canEdit = !editing ? true : (isManager || isCoAssignee);
  const canReassign = isManager && editing;

  // Manager-only quick DELETE. Confirms before hitting the DELETE endpoint;
  // parent's onSaved refetches after we close so the task drops from every view.
  const handleDelete = async () => {
    if (!editing || !existing?.id) return;
    if (!window.confirm(`Delete "${existing.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/tasks/${existing.id}`);
      toast.success("Task deleted");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete task");
    } finally {
      setDeleting(false);
    }
  };

  // Whenever the dialog opens for an existing task, pull the timer state so
  // the "Edit total hours" input can prefill with the TASK's total (not just
  // this user's slot) — the override endpoint wipes everyone's sessions.
  useEffect(() => {
    if (!open || !editing || !existing?.id || !user?.id) return;
    api.get(`/tasks/${existing.id}/timer`).then(({ data }) => {
      const secs = data?.total_seconds ?? 0;
      setMyTrackedSeconds(secs);
      setManualHours((secs / 3600).toFixed(2));
    }).catch(() => {
      setMyTrackedSeconds(0);
      setManualHours("0.00");
    });
  }, [open, editing, existing?.id, user?.id, timerRefreshKey]);

  // Override THIS user's total on the task — the endpoint replaces every
  // manual correction session so the sum matches exactly what was typed.
  const handleSaveTotalHours = async () => {
    // Accept "1.5" and Indian-style "1,5" both — parseFloat only groks dots.
    const raw = String(manualHours ?? "").trim().replace(",", ".");
    const hours = parseFloat(raw);
    if (!Number.isFinite(hours) || hours < 0) {
      toast.error("Enter a valid non-negative number of hours");
      return;
    }
    if (hours > 720) {
      toast.error("Max 720 hours");
      return;
    }
    setAddingTime(true);
    try {
      await api.post(`/tasks/${existing.id}/time/set`, { hours, note: manualNote.trim() });
      toast.success(`Total time set to ${hours} h`);
      setManualNote("");
      setTimerRefreshKey((k) => k + 1);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update time");
    } finally {
      setAddingTime(false);
    }
  };

  // Weekly-report PDF picker → base64 data-URL upload.
  const handleWeeklyFilePick = (slot, file) => {
    if (!file) { setWeeklyFiles((s) => ({ ...s, [slot]: null })); return; }
    if (file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("PDF must be under 4 MB");
      return;
    }
    setWeeklyFiles((s) => ({ ...s, [slot]: file }));
  };

  const handleWeeklyUpload = async (slot) => {
    const file = weeklyFiles[slot];
    if (!file || !existing?.id) return;
    setWeeklyUploadingSlot(slot);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      const res = await api.post(`/tasks/${existing.id}/submission/upload?slot=${slot}`, {
        file_name: file.name,
        data_url: dataUrl,
      });
      toast.success(
        res.data?.complete
          ? "Both PDFs uploaded — task marked complete."
          : "Uploaded. Upload the other PDF to close this task.",
      );
      setWeeklyFiles((s) => ({ ...s, [slot]: null }));
      onSaved?.();
      if (res.data?.complete) onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to upload PDF");
    } finally {
      setWeeklyUploadingSlot("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        // send both the primary lead (first assignee) and the full multi-assignee list
        assigned_to: form.assignees[0] || form.assigned_to || null,
        assignees: form.assignees && form.assignees.length ? form.assignees : (form.assigned_to ? [form.assigned_to] : []),
        start_date: form.start_date || null,
        // Store deadline as end-of-day IST (23:59) so overdue checks stay sensible
        // even though we no longer expose a time picker to the user.
        deadline: form.deadline ? new Date(`${form.deadline}T23:59:00`).toISOString() : null,
      };
      if (payload.start_date && payload.deadline && payload.start_date > payload.deadline.slice(0, 10)) {
        toast.error("Start date can't be after the deadline.");
        setSaving(false);
        return;
      }
      // Title is mandatory for BOTH create and edit — HTML `required` catches
      // most cases but a trimmed check + toast is nicer than the browser's
      // native tooltip and covers copy-pasted whitespace.
      if (!payload.title || !payload.title.trim()) {
        toast.error("Task title is required.");
        setSaving(false);
        return;
      }
      // Client + Project are mandatory on new tasks. On edit they can stay as
      // they are (legacy tasks are grandfathered as Unspecified until retro-tagged).
      if (!editing) {
        if (!payload.client || !payload.client.trim()) {
          toast.error("Pick a client (or add a new one).");
          setSaving(false);
          return;
        }
        if (!payload.project_id) {
          toast.error("Pick a project (or add a new one).");
          setSaving(false);
          return;
        }
      }
      if (!editing && (!payload.assignees || payload.assignees.length === 0)) {
        toast.error("Pick at least one assignee.");
        setSaving(false);
        return;
      }
      // Empty string project_id would be interpreted as "un-tag" server-side; drop it on edit.
      if (editing && !payload.project_id) delete payload.project_id;
      if (editing) {
        // Editors can only update non-assignee fields; strip if not manager & would try transfer
        if (!isManager) delete payload.assigned_to;
        const { data: saved } = await api.patch(`/tasks/${existing.id}`, payload);
        toast.success("Task updated");
        // If the caller just flipped this task to Complete via the dialog,
        // always surface the wrap-up popup so today's hours + optional links
        // get captured (matches MyTasks / Whiteboard behaviour).
        if (payload.status === "complete" && existing.status !== "complete") {
          onTaskCompleted?.(saved);
        }
      } else {
        const { data: created } = await api.post("/tasks", payload);
        toast.success(payload.start_date || payload.deadline ? "Task assigned" : "Task saved to Up Next");
        if (payload.status === "complete") {
          onTaskCompleted?.(created);
        }
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save task");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {editing ? "Edit Task" : "Assign New Task"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {editing
              ? (canReassign ? "Change fields or transfer this task to another team member." : "Update the fields you own.")
              : "Assign this task to a member of your team."}
          </DialogDescription>
        </DialogHeader>

        {!canEdit ? (
          <div className="text-sm text-white/60 p-3 border border-white/5 rounded-md">
            You don&apos;t have permission to edit this task.
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          {/* Monday-morning Weekly Submission uploader — one task per group,
              TWO PDF slots per task. `submission_group` = "weekly" | "social".
              The task auto-completes when both slots are uploaded (backend
              flag on POST /submission/upload?slot=). Legacy tasks flagged
              via `is_weekly_report` fall back to the weekly group. */}
          {editing && (existing?.submission_group || existing?.is_weekly_report) && (() => {
            const group = existing.submission_group || "weekly";
            const groupMeta = {
              weekly: {
                label: "Weekly submission",
                heading: "Upload both PDFs to close this task",
                slots: [
                  { key: "report", label: "Last week's report", hint: "Download the report + leaderboard PDF from Weekly & Leaderboard.", cta: "Weekly & Leaderboard", href: "/weekly-reports" },
                  { key: "plan",   label: "This week's plan",   hint: "Plan tasks in Whiteboard → Weekly Grid, then download the Plan PDF.", cta: "Weekly Grid", href: "/whiteboard" },
                ],
              },
              social: {
                label: "Social calendar submission",
                heading: "Upload both PDFs to close this task",
                slots: [
                  { key: "report", label: "Last week's social calendar", hint: "Download last week's social calendar PDF and mark which posts went live.", cta: "Social Calendar", href: "/social-calendar" },
                  { key: "plan",   label: "This week's social calendar", hint: "Fill this week's posts on Social Calendar, then download the PDF.",       cta: "Social Calendar", href: "/social-calendar" },
                ],
              },
            }[group];
            const uploads = existing.submission_uploads || {};
            // Legacy row without `submission_uploads`: hydrate report slot
            // from the old single-file field so users don't lose their upload.
            if (!existing.submission_uploads && (existing.weekly_report_upload || existing.submission_upload)) {
              uploads.report = existing.weekly_report_upload || existing.submission_upload;
            }
            const bothDone = uploads.report?.file_name && uploads.plan?.file_name;
            return (
              <div
                data-testid="task-dialog-weekly-upload-panel"
                data-submission-group={group}
                className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4 space-y-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-amber-300 font-mono">{groupMeta.label}</div>
                    <div className="font-display text-lg text-white mt-0.5">{groupMeta.heading}</div>
                  </div>
                  <span className={`text-[0.7rem] uppercase tracking-widest font-mono shrink-0 ${bothDone ? "text-emerald-400" : "text-amber-300"}`}>
                    {bothDone ? "✓ Complete" : `${Object.values(uploads).filter((u) => u?.file_name).length}/2 uploaded`}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groupMeta.slots.map((slot) => {
                    const uploaded = uploads[slot.key];
                    const picked = weeklyFiles[slot.key];
                    const busy = weeklyUploadingSlot === slot.key;
                    return (
                      <div
                        key={slot.key}
                        data-testid={`task-dialog-slot-${slot.key}`}
                        className={`rounded-md border p-3 space-y-2 ${uploaded ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-black/30"}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs uppercase tracking-widest text-white/60 font-mono">{slot.label}</div>
                          {uploaded && <span className="text-[0.7rem] uppercase tracking-widest text-emerald-400 font-mono">✓ Uploaded</span>}
                        </div>
                        <p className="text-xs text-white/50 leading-relaxed">
                          {slot.hint}{" "}
                          <a href={slot.href} className="underline hover:text-white" target="_blank" rel="noreferrer">
                            Open {slot.cta}
                          </a>
                        </p>

                        {uploaded?.file_name ? (
                          <div className="flex items-center justify-between rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-2 py-1.5">
                            <span className="text-xs text-white/70 truncate">{uploaded.file_name}</span>
                            <a
                              href={uploaded.data_url}
                              download={uploaded.file_name}
                              className="text-[0.7rem] uppercase tracking-widest text-emerald-300 hover:text-white shrink-0 ml-2"
                              data-testid={`task-dialog-slot-${slot.key}-download`}
                            >
                              PDF
                            </a>
                          </div>
                        ) : null}

                        <div className="flex items-center gap-2">
                          <label
                            htmlFor={`slot-input-${slot.key}`}
                            className="flex-1 cursor-pointer rounded border border-dashed border-amber-500/40 bg-black/30 px-2.5 py-1.5 text-xs text-white/70 hover:border-amber-400 hover:text-white transition truncate"
                            data-testid={`task-dialog-slot-${slot.key}-label`}
                          >
                            {picked ? picked.name : "Choose PDF…"}
                          </label>
                          <input
                            id={`slot-input-${slot.key}`}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            data-testid={`task-dialog-slot-${slot.key}-input`}
                            onChange={(e) => handleWeeklyFilePick(slot.key, e.target.files?.[0] || null)}
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleWeeklyUpload(slot.key)}
                            disabled={!picked || busy}
                            data-testid={`task-dialog-slot-${slot.key}-upload-btn`}
                            className="bg-amber-500 hover:bg-amber-400 text-black font-medium rounded disabled:opacity-40 h-8 text-xs px-3"
                          >
                            {busy ? "…" : uploaded ? "Replace" : "Upload"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* When editing an existing task, surface the timer widget right at
              the top so anyone opening the task can see the current play /
              pause / stop state AND fire the manual "add time" flow without
              closing the dialog. Hidden for new tasks (nothing to time yet). */}
          {editing && existing?.id && (
            <div className="rounded-md border border-white/10 bg-black/25 p-3 space-y-3" data-testid="task-dialog-timer-panel">
              <div className="overline text-white/50">Time tracker</div>
              <TaskTimer
                key={`timer-${existing.id}-${timerRefreshKey}`}
                task={existing}
                currentUser={user}
                isManager={isManager}
                onStatusChange={(t, newStatus) => {
                  // If the timer's "Finish" button just flipped this task to
                  // complete, surface the hours + delivery-links popup so
                  // today's hours get logged before the dialog closes.
                  if (newStatus === "complete" && existing.status !== "complete") {
                    onTaskCompleted?.(existing);
                  }
                  onSaved?.();
                }}
              />
              {/* Inline "log minutes I forgot to track" quick form. Visible for
                  every assignee (and managers via full-access) so nobody has to
                  hunt for the tiny + icon on the timer chip. */}
              <div className="border-t border-white/5 pt-3">
                <div className="text-xs uppercase tracking-widest text-white/50 mb-2 flex items-center justify-between">
                  <span>Set task total hours (overrides everyone)</span>
                  <span className="text-[0.7rem] text-white/40 normal-case tracking-normal">
                    Currently: {(myTrackedSeconds / 3600).toFixed(2)} h
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch gap-2">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 17.5"
                    value={manualHours}
                    data-testid="task-dialog-manual-hours"
                    onChange={(e) => setManualHours(e.target.value)}
                    className="bg-black/40 border-white/10 sm:w-40 focus-visible:ring-red-500"
                  />
                  <Input
                    placeholder="Why the correction? (optional)"
                    value={manualNote}
                    data-testid="task-dialog-manual-note"
                    onChange={(e) => setManualNote(e.target.value)}
                    className="bg-black/40 border-white/10 flex-1 focus-visible:ring-red-500"
                  />
                  <Button
                    type="button"
                    onClick={handleSaveTotalHours}
                    disabled={addingTime}
                    data-testid="task-dialog-add-minutes-btn"
                    className="bg-emerald-600 hover:bg-emerald-500 rounded-md"
                  >
                    {addingTime ? "Saving…" : "Save"}
                  </Button>
                </div>
                <p className="text-[0.7rem] text-white/40 mt-1.5">
                  This becomes the task&apos;s single source of truth — clears every prior timer / manual session across every assignee and replaces them with the number you type. Reflects instantly in the Projects rollup.
                </p>
              </div>
            </div>
          )}

          {/* CLIENT — pick from the master list or add a new one. Mandatory. */}
          <div>
            <Label className="overline">Client <span className="text-[#E11D2E]">*</span></Label>
            {!addingClient ? (
              <div className="flex items-stretch gap-2 mt-2">
                <Select
                  value={form.client || undefined}
                  onValueChange={(v) => setForm({ ...form, client: v, project_id: "" })}
                >
                  <SelectTrigger
                    data-testid={TID.taskDialogClient || "task-dialog-client"}
                    className="bg-black/40 border-white/10 flex-1"
                  >
                    <SelectValue placeholder="Select a client…" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white max-h-64">
                    {clients.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`task-dialog-client-opt-${c.replace(/\W+/g, "-")}`}>{c}</SelectItem>
                    ))}
                    {clients.length === 0 && <div className="text-xs text-white/40 px-3 py-2">No clients yet — add one →</div>}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="task-dialog-add-client-btn"
                  className="border-white/15 hover:border-[#E11D2E] hover:text-[#E11D2E]"
                  onClick={() => setAddingClient(true)}
                >+ New</Button>
              </div>
            ) : (
              <div className="flex items-stretch gap-2 mt-2">
                <Input
                  autoFocus
                  data-testid="task-dialog-new-client-input"
                  value={newClient}
                  onChange={(e) => setNewClient(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreateClient(); } }}
                  placeholder="New client name"
                  className="bg-black/40 border-white/10 flex-1"
                />
                <Button type="button" data-testid="task-dialog-new-client-save" onClick={handleCreateClient} className="bg-[#E11D2E] hover:bg-[#c11024]">Add</Button>
                <Button type="button" variant="outline" onClick={() => { setAddingClient(false); setNewClient(""); }} className="border-white/15">Cancel</Button>
              </div>
            )}
          </div>

          {/* PROJECT — nested under Client. Mandatory for new tasks. */}
          <div>
            <Label className="overline flex items-center gap-2">
              Project {!editing && <span className="text-[#E11D2E]">*</span>}
              <span className="text-[0.7rem] text-white/40 normal-case tracking-normal">
                (all hours across every task with this project will roll up together)
              </span>
            </Label>
            {!addingProject ? (
              <div className="flex items-stretch gap-2 mt-2">
                <Select
                  value={form.project_id || undefined}
                  onValueChange={(v) => setForm({ ...form, project_id: v })}
                  disabled={!form.client}
                >
                  <SelectTrigger
                    data-testid="task-dialog-project"
                    className="bg-black/40 border-white/10 flex-1 disabled:opacity-40"
                  >
                    <SelectValue placeholder={form.client ? "Select a project…" : "Pick a client first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white max-h-64">
                    {projectsForClient.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`task-dialog-project-opt-${p.id}`}>{p.name}</SelectItem>
                    ))}
                    {form.client && projectsForClient.length === 0 && (
                      <div className="text-xs text-white/40 px-3 py-2">No projects for {form.client} yet — add one →</div>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!form.client}
                  data-testid="task-dialog-add-project-btn"
                  className="border-white/15 hover:border-[#E11D2E] hover:text-[#E11D2E] disabled:opacity-40"
                  onClick={() => setAddingProject(true)}
                >+ New</Button>
              </div>
            ) : (
              <div className="flex items-stretch gap-2 mt-2">
                <Input
                  autoFocus
                  data-testid="task-dialog-new-project-input"
                  value={newProject}
                  onChange={(e) => setNewProject(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreateProject(); } }}
                  placeholder={`New project for ${form.client}`}
                  className="bg-black/40 border-white/10 flex-1"
                />
                <Button type="button" data-testid="task-dialog-new-project-save" onClick={handleCreateProject} className="bg-[#E11D2E] hover:bg-[#c11024]">Add</Button>
                <Button type="button" variant="outline" onClick={() => { setAddingProject(false); setNewProject(""); }} className="border-white/15">Cancel</Button>
              </div>
            )}
          </div>

          <div>
            <Label className="overline">Task Title <span className="text-[#E11D2E]">*</span></Label>
            <Input
              required
              data-testid={TID.taskDialogTitle}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
              placeholder="e.g. 60-second brand reel — cut v1"
            />
            {!editing && similarMatches.length > 0 && (
              <div
                data-testid="task-dialog-similar-hint"
                className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2"
              >
                <div className="text-xs uppercase tracking-widest text-amber-300 mb-1.5">
                  {similarMatches.length} similar open task{similarMatches.length > 1 ? "s" : ""} already exist — reuse instead?
                </div>
                <div className="space-y-1">
                  {similarMatches.map((m) => (
                    <button
                      type="button"
                      key={m.id}
                      data-testid={`task-dialog-similar-${m.id}`}
                      onClick={() => {
                        // Prefill from the match so the user can keep this dialog
                        // OR simply close and open the existing task via `onSaved` refresh.
                        setForm((f) => ({
                          ...f,
                          title: m.title,
                          client: m.client || f.client,
                          project_id: m.project_id || f.project_id,
                        }));
                        toast(`Reusing "${m.title}" — save to log more time under it.`, { icon: "↻" });
                      }}
                      className="w-full text-left text-xs px-2 py-1 rounded bg-black/30 hover:bg-black/50 border border-white/5 hover:border-amber-500/40 transition flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        <span className="text-white/90">{m.title}</span>
                        <span className="text-white/40"> · {m.client || "—"} · {m.assigned_to_name || "unassigned"}</span>
                      </span>
                      <span className="text-[0.65rem] uppercase tracking-widest text-white/50 shrink-0">{(m.status || "").replace("_", " ")}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Start Date</Label>
              <Input
                type="date"
                data-testid="task-dialog-start-date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                onClick={(e) => { if (typeof e.currentTarget.showPicker === "function") { try { e.currentTarget.showPicker(); } catch (_) { /* not supported */ } } }}
                className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500 [color-scheme:dark]"
              />
            </div>
            <div>
              <Label className="overline">Deadline</Label>
              <Input
                type="date"
                data-testid={TID.taskDialogDeadline}
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                onClick={(e) => { if (typeof e.currentTarget.showPicker === "function") { try { e.currentTarget.showPicker(); } catch (_) { /* not supported */ } } }}
                className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500 [color-scheme:dark]"
              />
            </div>
          </div>
          <p className="text-xs text-white/40 -mt-2">
            Leave both dates blank to park this in the member&apos;s <span className="text-[#E11D2E]">Up Next</span> list.
          </p>

          {(isManager && (!editing || canReassign)) ? (
            <div>
              <Label className="overline">{editing ? "Assignees" : "Assign to (multi-select)"}</Label>
              <div className="mt-2 flex flex-wrap gap-2 border border-white/10 rounded-md p-2 bg-black/40 min-h-[52px]">
                {form.assignees.length === 0 && (
                  <span className="text-xs text-white/40 py-1 px-1">Select one or more team members below.</span>
                )}
                {form.assignees.map((uid, idx) => {
                  const u = users.find((x) => x.id === uid);
                  return (
                    <span
                      key={uid}
                      data-testid={`task-dialog-assignee-chip-${uid}`}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                        idx === 0 ? "border-[#E11D2E]/40 bg-[#E11D2E]/10 text-[#E11D2E]" : "border-white/15 bg-white/5 text-white/80"
                      }`}
                    >
                      {u?.name || uid.slice(-6)}{idx === 0 && <span className="text-[0.65rem] uppercase tracking-widest">lead</span>}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, assignees: form.assignees.filter((x) => x !== uid) })}
                        className="hover:text-white ml-1"
                        aria-label="Remove"
                      >×</button>
                    </span>
                  );
                })}
              </div>
              <Select
                value=""
                onValueChange={(v) => {
                  if (!v || form.assignees.includes(v)) return;
                  setForm({ ...form, assignees: [...form.assignees, v] });
                }}
              >
                <SelectTrigger data-testid={TID.taskDialogAssignee} className="bg-black/40 border-white/10 mt-2">
                  <SelectValue placeholder="+ Add a team member" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  {users.filter((u) => !form.assignees.includes(u.id)).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.team}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-white/40 mt-1.5">
                First picked is the lead. Every assignee gets their own status + timer; the task is only complete when <em>all</em> are done.
              </p>
            </div>
          ) : (!editing && !isManager) ? (
            /* Editors self-assign only. The chip below signals "you own this" and
               matches the backend rule that rejects any other assignee. */
            <div>
              <Label className="overline">Assigned to</Label>
              <div
                data-testid="task-dialog-self-assigned"
                className="mt-2 flex items-center gap-2 border border-[#E11D2E]/30 bg-[#E11D2E]/5 rounded-md p-3"
              >
                <span className="text-xs text-[#E11D2E] font-mono uppercase tracking-widest">You</span>
                <span className="text-sm text-white/80 truncate">{user?.name}</span>
                <span className="ml-auto text-[0.7rem] text-white/40 font-mono uppercase tracking-widest">Self-assigned</span>
              </div>
              <p className="text-xs text-white/40 mt-1.5">
                Members can only create tasks for themselves. Ask a manager to assign work to others.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="overline">NAS Workfile URL</Label>
              <Input
                data-testid={TID.taskDialogNas}
                value={form.nas_link}
                onChange={(e) => setForm({ ...form, nas_link: e.target.value })}
                className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                placeholder="//nas/projects/…"
              />
            </div>
            <div>
              <Label className="overline">Final Video Link</Label>
              <Input
                data-testid="task-dialog-final-link"
                value={form.final_video_link}
                onChange={(e) => setForm({ ...form, final_video_link: e.target.value })}
                className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                placeholder="Drive / Wistia / Vimeo…"
              />
            </div>
          </div>

          <div>
            <Label className="overline">Notes</Label>
            <Textarea
              data-testid={TID.taskDialogDesc}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-black/40 border-white/10 mt-2 min-h-[80px] focus-visible:ring-red-500"
              placeholder="Brief the editor…"
            />
          </div>

          <div>
            <Label className="overline">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="task-dialog-status" className="bg-black/40 border-white/10 mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="carry_forward">Carry Forward</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-white/40 mt-1.5">
              Sets the initial status for every assignee on this task.
            </p>
          </div>

          {isManager && (
            <div className="border border-white/10 rounded-md p-3" data-testid={TID.taskDialogPriority}>
              <div className="text-sm font-medium mb-2">Priority</div>
              <div className="grid grid-cols-4 gap-1.5">
                {PRIORITY_ORDER.map((k) => {
                  const on = form.priority_level === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      data-testid={`task-dialog-priority-${k}`}
                      aria-pressed={on}
                      onClick={() => setForm({ ...form, priority_level: k, priority: k === "urgent" || k === "high" })}
                      className={`h-9 rounded-md border text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${on ? "border-[#E11D2E] bg-[#E11D2E]/10 text-white" : "border-white/10 text-white/60 hover:bg-white/5"}`}
                    >
                      <Flag size={12} className={PRIORITY_META[k].text} fill="currentColor" /> {PRIORITY_META[k].label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-row items-center justify-between gap-2 sm:justify-between">
            {editing && isManager ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleDelete}
                disabled={deleting || saving}
                data-testid="task-dialog-delete-btn"
                className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-full px-5"
              >
                {deleting ? "Deleting…" : "Delete Task"}
              </Button>
            ) : <div />}
            <Button
              type="submit"
              disabled={saving || deleting}
              data-testid={TID.taskDialogSubmit}
              className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6"
            >
              {saving ? "Saving…" : editing ? "Update Task" : "Assign Task"}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
